import { skipToken, useQuery } from '@tanstack/react-query'
import { memo, type ReactNode, useEffect, useMemo, useState } from 'react'

import type { CustomerContractDescription } from '@orus.eu/backend'
import { ensureError, TechnicalError } from '@orus.eu/error'
import { isPharaohTheme, usePharaohTheme, useSetCoBranding } from '@orus.eu/pharaoh'

import { useTRPC } from '../../client.ts'
import { coBrandingFactories, EmbeddingPartnerProvider } from '../../lib/embedding-partner.ts'
import { logger } from '../../lib/logger.ts'
import { useSession } from '../../lib/session.ts'

export const EmbeddingPartnerWrapper = memo<{ children: ReactNode }>(function EmbeddingPartnerWrapper({ children }) {
  const trpc = useTRPC()

  const session = useSession()

  const [explicitEmbeddingPartnerId, setExplicitEmbeddingPartnerId] = useState<string | undefined>(
    getInitialEmbeddingPartnerId(),
  )
  const setCoBranding = useSetCoBranding()
  const { setTheme } = usePharaohTheme()

  const hasPermissions =
    session?.permissions.type === 'client' &&
    session.permissions.rolesPermissions.flatMap((r) => r.permissions).includes('contract.read')

  const { data: contracts } = useQuery(
    trpc.contracts.listMyContracts.queryOptions(hasPermissions ? undefined : skipToken),
  )

  const embeddingPartnerId = explicitEmbeddingPartnerId ?? getDefaultEmbeddingPartnerId(contracts)

  // This effect reacts to embeddingPartner changes to update the co-branding accordingly
  useEffect(() => {
    if (!embeddingPartnerId) {
      setCoBranding(null)
      setTheme('light')
      return
    }

    const coBrandingFactory = coBrandingFactories[embeddingPartnerId]
    if (!coBrandingFactory) {
      // we have no co-branding registered for it, so we fall back to an unbranded Orus funnel rather than crashing.
      // (the partner stays associated with the subscription, organization is preserved)
      logger.error(
        new TechnicalError('No co-branding registered for embedding partner', {
          context: { embeddingPartnerId },
        }),
      )
      setCoBranding(null)
      setTheme('light')
      return
    }

    if (isPharaohTheme(embeddingPartnerId)) setTheme(embeddingPartnerId)

    let cancelled = false

    coBrandingFactory()
      .then((coBranding) => {
        if (cancelled) {
          return
        }

        setCoBranding(coBranding)
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return
        }

        logger.warning('Error while loading co-branding', {
          errStack: ensureError(err).stack ?? null,
        })
      })

    return () => {
      cancelled = true
    }
  }, [embeddingPartnerId, setCoBranding, setTheme])

  const embeddingPartnerContextValue = useMemo(
    () => ({ embeddingPartnerId, setEmbeddingPartnerId: setExplicitEmbeddingPartnerId }),
    [embeddingPartnerId, setExplicitEmbeddingPartnerId],
  )

  return <EmbeddingPartnerProvider value={embeddingPartnerContextValue}>{children}</EmbeddingPartnerProvider>
})

/**
 * Gets the default embedding partner id, based on the presence or absence of a contract, to use until
 * a different embedding partner id is explicitly set.
 */
function getDefaultEmbeddingPartnerId(contracts: CustomerContractDescription[] | undefined): string | undefined {
  if (!contracts) {
    // loading, or failed and waiting for redirect
    return getInitialEmbeddingPartnerId()
  }

  if (contracts.length < 1) {
    // no contract => no embedding partner
    return undefined
  }

  const firstContract = contracts[0]
  const embeddingPartner = firstContract.embeddingPartner
  return embeddingPartner === 'none' ? undefined : embeddingPartner.id
}

/**
 * Gets the initial embedding partner id from the URL parameters, to avoid a flash while loading
 */
function getInitialEmbeddingPartnerId(): string | undefined {
  const url = new URL(document.location.href)

  const embeddingPartnerParameter = url.searchParams.get('embedding_partner')
  if (embeddingPartnerParameter && embeddingPartnerParameter in coBrandingFactories) {
    return embeddingPartnerParameter
  }

  return undefined
}
